import React, { useEffect, useState, Fragment } from 'react';
import { XIcon, SaveIcon, AlertCircleIcon, CheckCircleIcon, InfoIcon } from 'lucide-react';
interface ProductVariant {
  id: string;
  model: string;
  storage: string;
  color: string;
  image: string;
  prices: {
    new: number | null;
    excellence: number | null;
    good: number | null;
    fair: number | null;
  };
  quantities: {
    new: number | null;
    excellence: number | null;
    good: number | null;
    fair: number | null;
  };
}
interface BulkPriceEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: {
    id: string;
    name: string;
    variants: ProductVariant[];
  } | null;
  onSave: (variants: ProductVariant[]) => void;
}
// Mock inventory data to find related SKUs
const inventory = [{
  id: 'iphone-12',
  name: 'iPhone 12',
  variants: [{
    id: 'iphone-12-128gb-black',
    model: 'iPhone 12',
    storage: '128GB',
    color: 'Black',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/a1zZe5YfTvqp2TmKQmQWqk/image.png',
    prices: {
      new: 1088,
      excellence: 988,
      good: 888,
      fair: 788
    },
    quantities: {
      new: 1,
      excellence: 5,
      good: 10,
      fair: 3
    }
  }, {
    id: 'iphone-12-256gb-black',
    model: 'iPhone 12',
    storage: '256GB',
    color: 'Black',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/a1zZe5YfTvqp2TmKQmQWqk/image.png',
    prices: {
      new: 1088,
      excellence: 988,
      good: 888,
      fair: 788
    },
    quantities: {
      new: 1,
      excellence: 2,
      good: 7,
      fair: 5
    }
  }, {
    id: 'iphone-12-512gb-black',
    model: 'iPhone 12',
    storage: '512GB',
    color: 'Black',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/a1zZe5YfTvqp2TmKQmQWqk/image.png',
    prices: {
      new: 1088,
      excellence: 988,
      good: 888,
      fair: 788
    },
    quantities: {
      new: 1,
      excellence: 0,
      good: 2,
      fair: 8
    }
  }]
}, {
  id: 'samsung-s24',
  name: 'Samsung Galaxy S24',
  variants: [{
    id: 'samsung-s24-128gb-black',
    model: 'Samsung Galaxy S24',
    storage: '128GB',
    color: 'Black',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9CLQER3mW4v2Jv4RPGHc1W/image.png',
    prices: {
      new: 999,
      excellence: 899,
      good: 799,
      fair: 699
    },
    quantities: {
      new: 3,
      excellence: 7,
      good: 12,
      fair: 5
    }
  }, {
    id: 'samsung-s24-256gb-black',
    model: 'Samsung Galaxy S24',
    storage: '256GB',
    color: 'Black',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9CLQER3mW4v2Jv4RPGHc1W/image.png',
    prices: {
      new: 1099,
      excellence: 999,
      good: 899,
      fair: 799
    },
    quantities: {
      new: 2,
      excellence: 3,
      good: 4,
      fair: 1
    }
  }]
}, {
  id: 'macbook-air-m2',
  name: 'MacBook Air M2',
  variants: [{
    id: 'macbook-air-m2-256gb-gray',
    model: 'MacBook Air M2',
    storage: '256GB',
    color: 'Space Gray',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/qcMnDWqru55oVRbBxJg3SM/image.png',
    prices: {
      new: 1299,
      excellence: 1099,
      good: 999,
      fair: 899
    },
    quantities: {
      new: 2,
      excellence: 5,
      good: 7,
      fair: 4
    }
  }]
}];
export const BulkPriceEditModal: React.FC<BulkPriceEditModalProps> = ({
  isOpen,
  onClose,
  product,
  onSave
}) => {
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [isShowingAllVariants, setIsShowingAllVariants] = useState(false);
  useEffect(() => {
    if (product) {
      // If we received a product with only one variant, it might be a specific SKU
      if (product.variants.length === 1) {
        // Find the parent product to get all related variants
        const parentProduct = inventory.find(p => p.variants.some(v => v.id === product.variants[0].id));
        if (parentProduct) {
          setVariants(parentProduct.variants);
          setSelectedVariantId(product.variants[0].id);
          setIsShowingAllVariants(true);
        } else {
          setVariants(product.variants);
          setSelectedVariantId(product.variants[0].id);
          setIsShowingAllVariants(false);
        }
      } else {
        // If we received a product with multiple variants, show them all
        setVariants(product.variants);
        setSelectedVariantId(null);
        setIsShowingAllVariants(true);
      }
    }
  }, [product]);
  const handlePriceChange = (variantIndex: number, condition: string, value: string) => {
    const newVariants = [...variants];
    const numValue = value === '' ? null : parseFloat(value);
    newVariants[variantIndex].prices[condition] = numValue;
    setVariants(newVariants);
  };
  const handleQuantityChange = (variantIndex: number, condition: string, value: string) => {
    const newVariants = [...variants];
    const numValue = value === '' ? null : parseInt(value);
    newVariants[variantIndex].quantities[condition] = numValue;
    setVariants(newVariants);
  };
  const handleSave = () => {
    setIsSaving(true);
    setSaveSuccess(false);
    setSaveError(false);
    // Simulate API call
    setTimeout(() => {
      try {
        onSave(variants);
        setIsSaving(false);
        setSaveSuccess(true);
        // Close modal after success message
        setTimeout(() => {
          onClose();
          setSaveSuccess(false);
        }, 1500);
      } catch (error) {
        setIsSaving(false);
        setSaveError(true);
      }
    }, 1000);
  };
  // Apply a price to all cells in a row (all conditions for a variant)
  const applyPriceToRow = (variantIndex: number, baseCondition: string) => {
    // Function removed but kept as empty to avoid breaking existing code
  };
  // Apply a price to all cells in a column (same condition across all variants)
  const applyPriceToColumn = (condition: string, baseVariantIndex: number) => {
    // Function removed but kept as empty to avoid breaking existing code
  };
  if (!isOpen || !product) return null;
  const conditions = ['new', 'excellence', 'good', 'fair'];
  const conditionLabels = {
    new: 'New',
    excellence: 'Excellence',
    good: 'Good',
    fair: 'Fair'
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1007">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col" data-id="element-1008">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1009">
          <div data-id="element-1010">
            <h2 className="text-xl font-semibold text-gray-800" data-id="element-1011">
              Edit Prices - {product.name}
            </h2>
            {selectedVariantId && <p className="text-sm text-gray-500 mt-1" data-id="element-1012">
                {isShowingAllVariants ? 'Showing all related variants for this product' : 'Showing only the selected variant'}
              </p>}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 focus:outline-none" disabled={isSaving} data-id="element-1013">
            <XIcon className="h-5 w-5" data-id="element-1014" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-auto flex-grow p-6" data-id="element-1015">
          {saveSuccess && <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md flex items-center" data-id="element-1016">
              <CheckCircleIcon className="h-5 w-5 mr-2" data-id="element-1017" />
              Prices updated successfully!
            </div>}
          {saveError && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center" data-id="element-1018">
              <AlertCircleIcon className="h-5 w-5 mr-2" data-id="element-1019" />
              There was an error updating prices. Please try again.
            </div>}

          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6" data-id="element-1020">
            <div className="flex items-start" data-id="element-1021">
              <InfoIcon className="h-5 w-5 text-blue-500 mr-2 mt-0.5" data-id="element-1022" />
              <div data-id="element-1023">
                <p className="text-blue-700 font-medium" data-id="element-1024">Batch Price Editing</p>
                <p className="text-blue-600 text-sm mt-1" data-id="element-1025">
                  You can edit prices for all product variants at once. Changes
                  will only be applied after you click "Save Changes".
                </p>
                <div className="text-blue-600 text-sm mt-2" data-id="element-1026">
                  <span className="font-medium" data-id="element-1027">Pro tip:</span> Click on the
                  "Apply to Row" or "Apply to Column" buttons to quickly copy
                  prices.
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto" data-id="element-1028">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1029">
              <thead className="bg-gray-50" data-id="element-1030">
                <tr data-id="element-1031">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-64" data-id="element-1032">
                    Product
                  </th>
                  {conditions.map(condition => <th key={condition} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1033">
                      {conditionLabels[condition]}
                    </th>)}
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32" data-id="element-1034">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1035">
                {variants.map((variant, variantIndex) => <Fragment key={variant.id} data-id="element-1036">
                    {/* Price row */}
                    <tr className={variant.id === selectedVariantId ? 'bg-blue-50' : ''} data-id="element-1037">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1038">
                        <div className="flex items-center" data-id="element-1039">
                          <div className="flex-shrink-0 h-14 w-14 bg-gray-100 rounded-md flex items-center justify-center" data-id="element-1040">
                            {variant.image ? <img src={variant.image} alt={variant.model} className="h-12 w-12 object-contain" data-id="element-1041" /> : <div className="text-gray-400 text-xs" data-id="element-1042">
                                No image
                              </div>}
                          </div>
                          <div className="ml-4" data-id="element-1043">
                            <div className="text-sm font-medium text-gray-900" data-id="element-1044">
                              {variant.model}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-1045">
                              {variant.storage} - {variant.color}
                            </div>
                          </div>
                        </div>
                      </td>
                      {conditions.map(condition => <td key={`${variant.id}-${condition}-price`} className="px-6 py-2 whitespace-nowrap" data-id="element-1046">
                          <div className="flex flex-col items-center" data-id="element-1047">
                            <div className="text-xs text-gray-500 mb-1" data-id="element-1048">
                              Price ($)
                            </div>
                            <div className="relative rounded-md shadow-sm w-28" data-id="element-1049">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1050">
                                <span className="text-gray-500 sm:text-sm" data-id="element-1051">
                                  $
                                </span>
                              </div>
                              <input type="number" value={variant.prices[condition] === null ? '' : variant.prices[condition]} onChange={e => handlePriceChange(variantIndex, condition, e.target.value)} className={`focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-3 py-2 sm:text-sm border-gray-300 rounded-md ${variant.id === selectedVariantId ? 'border-blue-300 bg-blue-50' : ''}`} placeholder="0.00" min="0" step="0.01" disabled={isSaving} data-id="element-1052" />
                            </div>
                          </div>
                        </td>)}
                      <td className="px-3 py-2 whitespace-nowrap" data-id="element-1053">
                        <button onClick={() => applyPriceToRow(variantIndex, 'new')} className="w-full text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 py-1 px-2 rounded mb-1" title="Apply 'New' condition price to all conditions for this variant" data-id="element-1054">
                          Apply to Row
                        </button>
                      </td>
                    </tr>

                    {/* Quantity row */}
                    <tr className={`bg-gray-50 ${variant.id === selectedVariantId ? 'bg-blue-50/50' : ''}`} data-id="element-1055">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-1056">
                        Quantity
                      </td>
                      {conditions.map(condition => <td key={`${variant.id}-${condition}-qty`} className="px-6 py-2 whitespace-nowrap" data-id="element-1057">
                          <div className="flex justify-center" data-id="element-1058">
                            <input type="number" value={variant.quantities[condition] === null ? '' : variant.quantities[condition]} onChange={e => handleQuantityChange(variantIndex, condition, e.target.value)} className={`focus:ring-blue-500 focus:border-blue-500 block w-28 px-3 py-2 sm:text-sm border-gray-300 rounded-md ${variant.id === selectedVariantId ? 'border-blue-300 bg-blue-50' : ''}`} placeholder="0" min="0" disabled={isSaving} data-id="element-1059" />
                          </div>
                        </td>)}
                      <td className="px-3 py-2 whitespace-nowrap" data-id="element-1060">
                        {/* Empty cell for alignment */}
                      </td>
                    </tr>
                  </Fragment>)}

                {/* Column action row */}
                <tr className="bg-gray-100" data-id="element-1061">
                  <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-700" data-id="element-1062">
                    Column Actions
                  </td>
                  {conditions.map((condition, index) => <td key={`column-action-${condition}`} className="px-6 py-3 text-center" data-id="element-1063">
                      {/* Empty cell for alignment */}
                    </td>)}
                  <td className="px-3 py-3" data-id="element-1064">
                    {/* Empty cell for alignment */}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end" data-id="element-1065">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 mr-3" disabled={isSaving} data-id="element-1066">
            Cancel
          </button>
          <button onClick={handleSave} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center" disabled={isSaving} data-id="element-1067">
            {isSaving ? <>
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2" data-id="element-1068"></div>
                Saving...
              </> : <>
                <SaveIcon className="h-4 w-4 mr-2" data-id="element-1069" />
                Save All Changes
              </>}
          </button>
        </div>
      </div>
    </div>;
};